import { Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import type { MeterInput, NewMeter } from '@weft/types';
import { CRITERION_OPERATORS } from '@weft/types';
import React from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { CriterionSelect } from '../../CriterionSelect';
import { MeterFieldSelect } from '../../MeterFieldSelect';

type CriterionsItemsProps = {
  conditionIndex: number;
  onLastCriterionDelete?: () => void;
};

export const CriterionsItems: React.FC<CriterionsItemsProps> = ({ conditionIndex, onLastCriterionDelete }) => {
  const { control } = useFormContext<MeterInput, unknown, NewMeter>();

  const eventName = useWatch({
    control,
    name: 'eventName',
  });

  const criterions = useFieldArray({
    control,
    name: `filter.conditions.${conditionIndex}.criterions`,
  });

  const handleAddCriterion = () => {
    criterions.append({
      field: '',
      operator: CRITERION_OPERATORS.Contains,
      value: '',
    });
  };

  const handleDeleteCriterion = (index: number) => {
    if (criterions.fields.length === 1) {
      onLastCriterionDelete?.();

      return;
    }

    criterions.remove(index);
  };

  return (
    <>
      {criterions.fields.map((criterion, index) => (
        <Grid
          key={criterion.id}
          container
          item
          columnSpacing="12px"
        >
          {index === 0 && (
            <Grid
              item
              display="flex"
              alignItems="end"
              marginBottom="8px"
              width="54px"
              columnGap={0.5}
            >
              <Typography
                display="inline"
                variant="subtitle1"
              >
                When
              </Typography>
            </Grid>
          )}

          {index > 0 && (
            <Grid
              item
              display="flex"
              alignItems="end"
              marginBottom="8px"
              width="54px"
            >
              <Typography
                variant="subtitle1"
                color="secondary"
              >
                And
              </Typography>
            </Grid>
          )}

          <Grid
            item
            flex={1}
          >
            <Controller
              control={control}
              name={`filter.conditions.${conditionIndex}.criterions.${index}.field`}
              render={({ field }) => (
                <MeterFieldSelect
                  eventName={eventName}
                  value={field.value}
                  onChange={(event, newValue) => {
                    event.preventDefault();
                    field.onChange(newValue);
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            item
            flex={1}
          >
            <Controller
              control={control}
              name={`filter.conditions.${conditionIndex}.criterions.${index}.operator`}
              render={({ field }) => (
                <CriterionSelect
                  value={field.value}
                  onChange={(event, newValue) => {
                    event.preventDefault();
                    field.onChange(newValue);
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            item
            flex={1}
          >
            <Controller
              control={control}
              name={`filter.conditions.${conditionIndex}.criterions.${index}.value`}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  label="Value"
                  placeholder="Enter value"
                />
              )}
            />
          </Grid>

          <Grid
            item
            alignItems="end"
            display="flex"
            marginBottom="2px"
          >
            <IconButton onClick={() => handleDeleteCriterion(index)}>
              <VyIcon
                icon="Delete"
              />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Grid item>
        <Button
          variant="contained"
          color="secondary"
          size="medium"
          sx={theme => ({
            columnGap: '8px',
            paddingX: '20px',
            backgroundColor: '#fff',
            border: `1px solid ${theme.palette.divider}`,
          })}
          onClick={handleAddCriterion}
        >
          <VyIcon
            icon="PlusMedium"
            style={{
              transform: 'scale(0.8)',
            }}
          />
          And
        </Button>
      </Grid>
    </>
  );
};